import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NbDialogService, NbSelectModule, NbOptionModule, NbTooltipModule } from '@nebular/theme';
import { merge, Subscription } from 'rxjs';
import { first } from 'rxjs/operators';
import { AgGridAngular, ICellRendererAngularComp, ICellEditorAngularComp, IHeaderAngularComp } from 'ag-grid-angular';
import { ColDef, FilterChangedEvent, GridApi, GridOptions, GridReadyEvent, CellValueChangedEvent, IHeaderParams } from 'ag-grid-community';

import { APIService, KPI } from '../../../../@core/api';
import { GROUP_PERMISSION_EDIT_GAMME_SITE } from '../../../../@core/api/models';
import { ErrorService } from '../../../../@core/utils/error.service';
import { ScopeService, SpreadsheetColumnsService, UsersService, SaveToastService } from '../../../../@core/utils';
import { GAMME_PERIODICITY_LABELS, TABLE_FILTER_ICON } from '../../../../@core/utils/constants';
import { GammeSiteCommentDialogComponent } from './gamme-site-comment-dialog.component';
import { agIconLoadingTemplate } from '../../circular-spinner/circular-spinner';


@Component({
  selector: 'app-gamme-speech-header',
  template: `
    <svg style="width:22px;height:22px;opacity:0.5;flex-shrink:0" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M7 1C3.686 1 1 3.358 1 6.25c0 1.55.72 2.94 1.87 3.9L2.5 13l2.8-1.4A6.3 6.3 0 007 11.5c3.314 0 6-2.358 6-5.25S10.314 1 7 1z" stroke="#333" stroke-width="1.2" fill="none"/>
    </svg>`,
  styles: [':host { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; }'],
  standalone: true,
})
export class GammeSpeechHeaderComponent implements IHeaderAngularComp {
  params: IHeaderParams;
  agInit(params: IHeaderParams): void { this.params = params; }
  refresh(params: IHeaderParams): boolean { this.params = params; return true; }
}

@Component({
  selector: 'app-gamme-checkbox-renderer',
  template: `
    <label class="ag-cb" [class.ag-cb--disabled]="isBlocked" (click)="toggle($event)">
      <input type="checkbox" [checked]="params?.data?.is_active" [disabled]="isBlocked" />
    </label>`,
  styles: [':host { display: flex; align-items: center; justify-content: center; height: 100%; }'],
  standalone: true,
})
export class GammeCheckboxRendererComponent implements ICellRendererAngularComp {
  params: any;
  get isBlocked(): boolean {
    return this.params?.data?.gamme_is_active === false || !this.params?.context?.canEditGammeSite;
  }
  agInit(params: any): void { this.params = params; }
  refresh(params: any): boolean { this.params = params; return true; }
  toggle(event: MouseEvent): void {
    event.stopPropagation();
    if (this.isBlocked) return;
    this.params.context.toggleActive(this.params.data);
  }
}

@Component({
  selector: 'app-gamme-comment-renderer',
  template: `
    <span class="comment-btn" [class.has-content]="hasContent" [class.is-disabled]="!isActive" (click)="open()">
      (+)
    </span>`,
  styles: [`
    :host { display: flex; align-items: center; justify-content: center; height: 100%; }
    .comment-btn { padding: 0 2px; cursor: pointer; color: #aaa; font-weight: 600; font-size: 0.8rem; line-height: 1; user-select: none; }
    .comment-btn.has-content { color: #2d5f6e; }
    .comment-btn:hover { color: #1a3d47; }
    .comment-btn.is-disabled { cursor: not-allowed; opacity: 0.3; pointer-events: none; }
  `],
  standalone: true,
})
export class GammeCommentRendererComponent implements ICellRendererAngularComp {
  params: any;
  get hasContent(): boolean { return !!(this.params?.data?.comment_law || this.params?.data?.comment); }
  get isActive(): boolean { return !!this.params?.data?.is_active && this.params?.data?.gamme_is_active !== false; }
  agInit(params: any): void { this.params = params; }
  refresh(params: any): boolean { this.params = params; return true; }
  open(): void {
    if (!this.isActive) return;
    this.params.context.openCommentDialog(this.params.data);
  }
}

@Component({
  selector: 'app-gamme-reg-text-renderer',
  template: `
    <a *ngIf="url && isActive" [href]="url" target="_blank" rel="noopener noreferrer" class="text-link">{{ text || seeLabel }}</a>
    <span *ngIf="url && !isActive" class="text-link text-link--disabled">{{ text || seeLabel }}</span>
    <span *ngIf="!url && text" class="reg-ref">{{ text }}</span>
    <span *ngIf="!url && !text">—</span>`,
  styles: [`
    .text-link { color: #333; text-decoration: underline; font-size: 0.8rem; }
    .text-link--disabled { color: #aaa; text-decoration: underline; cursor: default; pointer-events: none; }
    .reg-ref { color: #555; font-size: 0.78rem; }
  `],
  standalone: true,
  imports: [CommonModule],
})
export class GammeRegTextRendererComponent implements ICellRendererAngularComp {
  params: any;
  get url(): string { return this.params?.data?.regulatory_text_url; }
  get text(): string { return this.params?.data?.regulatory_comment; }
  get seeLabel(): string { return this.params?.seeLabel ?? 'See'; }
  get isActive(): boolean { return !!this.params?.data?.is_active && this.params?.data?.gamme_is_active !== false; }
  agInit(params: any): void { this.params = params; }
  refresh(params: any): boolean { this.params = params; return true; }
}

@Component({
  selector: 'app-gamme-deliverable-renderer',
  template: `<span [class.deliverable-report]="isReport">{{ label }}</span>`,
  styles: [`.deliverable-report { color: #c0622a; font-weight: 700; }`],
  standalone: true,
})
export class GammeDeliverableRendererComponent implements ICellRendererAngularComp {
  params: any;
  get isReport(): boolean { return !!this.params?.data?.linked_document_type?.label; }
  get label(): string { return this.params?.data?.linked_document_type?.label ?? ''; }
  agInit(params: any): void { this.params = params; }
  refresh(params: any): boolean { this.params = params; return true; }
}

@Component({
  selector: 'app-gamme-service-provider-editor',
  template: `
    <input #input type="text" [value]="value" [attr.list]="listId" (input)="onInput($event)"
      style="width:100%;height:100%;border:none;outline:none;padding:0 6px;font-size:0.82rem;background:transparent;" />
    <datalist [id]="listId">
      <option *ngFor="let s of suggestions" [value]="s"></option>
    </datalist>`,
  standalone: true,
  imports: [CommonModule],
})
export class GammeServiceProviderEditorComponent implements ICellEditorAngularComp, AfterViewInit {
  @ViewChild('input') inputRef: ElementRef<HTMLInputElement>;
  params: any;
  value = '';
  suggestions: string[] = [];
  listId = '';

  agInit(params: any): void {
    this.params = params;
    this.value = params.value ?? '';
    this.suggestions = params.data?.suggested_companies ?? [];
    this.listId = `datalist-${params.data?.id ?? Math.random()}`;
  }

  ngAfterViewInit(): void {
    setTimeout(() => { this.inputRef?.nativeElement?.focus(); this.inputRef?.nativeElement?.select(); });
  }

  onInput(event: Event): void { this.value = (event.target as HTMLInputElement).value; }
  getValue(): string { return this.value; }
  refresh(): boolean { return false; }
  isPopup(): boolean { return false; }
}

@Component({
  selector: 'app-site-obligations-reglementaires',
  templateUrl: './site-obligations-reglementaires.component.html',
  styleUrls: ['./site-obligations-reglementaires.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    NbSelectModule,
    NbOptionModule,
    NbTooltipModule,
    AgGridAngular,
    GammeSpeechHeaderComponent,
    GammeCheckboxRendererComponent,
    GammeCommentRendererComponent,
    GammeRegTextRendererComponent,
    GammeDeliverableRendererComponent,
    GammeServiceProviderEditorComponent,
  ],
})
export class SiteObligationsReglementairesComponent implements OnInit, OnDestroy {
  @Input() public perimeterId: number;
  @Input() public perimeter: KPI.Perimeter;

  public loading = true;
  public rows: KPI.GammeSite[] = [];
  public buildings: KPI.Perimeter[] = [];
  public selectedBuildingId = 0;
  public canEditGammeSite = false;

  private gridApi: GridApi;
  private subscriptions: Subscription = new Subscription();

  public readonly periodicityLabels = GAMME_PERIODICITY_LABELS;


  public columnDefs: ColDef[] = [];
  public gridOptions: GridOptions;
  public filtersNb = 0;

  constructor(
    private api: APIService,
    private cdRef: ChangeDetectorRef,
    private dialogService: NbDialogService,
    private translate: TranslateService,
    private saveToast: SaveToastService,
    private errorService: ErrorService,
    private usersService: UsersService,
    private spreadsheetColumnsService: SpreadsheetColumnsService,
    private scopeService: ScopeService,
  ) { }

  public ngOnInit(): void {
    this.subscriptions.add(
      this.usersService.hasCurrentUserPermissions([GROUP_PERMISSION_EDIT_GAMME_SITE]).pipe(first()).subscribe(can => {
        this.canEditGammeSite = can;
        this.buildGrid();
        this.cdRef.markForCheck();
      }),
    );
    this.loadBuildings();
    this.subscriptions.add(
      this.spreadsheetColumnsService.getAgGridTranslations().subscribe(localeText => {
        this.gridOptions.localeText = localeText;
        this.cdRef.markForCheck();
      }),
    );
    this.subscriptions.add(
      merge(this.translate.onLangChange, this.translate.onTranslationChange).subscribe(() => {
        this.buildGrid();
        this.cdRef.markForCheck();
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private buildGrid(): void {
    const t = (key: string) => this.translate.instant(key);

    this.columnDefs = [
      {
        colId: 'is_active',
        field: 'is_active',
        headerName: '',
        headerTooltip: t('Activate / deactivate for this site'),
        cellRenderer: GammeCheckboxRendererComponent,
        width: 40, minWidth: 40, maxWidth: 40,
        sortable: false, filter: false, resizable: false,
        suppressColumnsToolPanel: true,
        cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
      },
      {
        field: 'label',
        headerName: t('Description'),
        minWidth: 250, flex: 3,
        tooltipField: 'label',
        cellStyle: { fontWeight: '500' },
        filter: 'agTextColumnFilter',
      },
      {
        field: 'periodicity',
        headerName: t('Periodicity'),
        width: 130,
        filter: 'agTextColumnFilter',
        valueFormatter: (p) => {
          const label = this.periodicityLabels[p.value as KPI.GammePeriodicity];
          if (!label || label === '—') return '';
          return t(label);
        },
      },
      {
        colId: 'reg_text',
        headerName: t('Regulatory texts'),
        width: 180,
        sortable: true,
        filter: 'agTextColumnFilter',
        cellRenderer: GammeRegTextRendererComponent,
        cellRendererParams: { seeLabel: t('See') },
      },
      {
        colId: 'comment',
        headerName: '',
        headerComponent: GammeSpeechHeaderComponent,
        cellRenderer: GammeCommentRendererComponent,
        width: 50,
        sortable: false, filter: false, resizable: false,
        suppressColumnsToolPanel: true,
        cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
      } as any,
      {
        field: 'theme.name',
        headerName: t('Theme'),
        width: 120,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'sub_theme.name',
        headerName: t('Sub-theme'),
        minWidth: 140, flex: 2,
        tooltipField: 'sub_theme.name',
        filter: 'agTextColumnFilter',
      },
      {
        field: 'site_staff',
        headerName: t('Site staff'),
        headerClass: 'vertical-header',
        width: 60,
        sortable: false,
        filter: false,
        cellRenderer: (p: any) => p.value ? '<span style="font-weight:600;color:#333">X</span>' : '',
        cellStyle: { textAlign: 'center' },
      },
      {
        field: 'maintenance_company',
        headerName: t('Maintenance company'),
        headerClass: 'vertical-header',
        width: 60,
        sortable: false,
        filter: false,
        cellRenderer: (p: any) => p.value ? '<span style="font-weight:600;color:#333">X</span>' : '',
        cellStyle: { textAlign: 'center' },
      },
      {
        field: 'approved_body',
        headerName: t('Approved body'),
        headerClass: 'vertical-header',
        width: 60,
        sortable: false,
        filter: false,
        cellRenderer: (p: any) => p.value ? '<span style="font-weight:600;color:#333">X</span>' : '',
        cellStyle: { textAlign: 'center' },
      },
      {
        field: 'safety_commission',
        headerName: t('Safety commission'),
        headerClass: 'vertical-header',
        width: 60,
        sortable: false,
        filter: false,
        cellRenderer: (p: any) => p.value ? '<span style="font-weight:600;color:#333">X</span>' : '',
        cellStyle: { textAlign: 'center' },
      },
      {
        field: 'linked_document_type.label',
        headerName: t('Document type'),
        width: 180,
        filter: 'agTextColumnFilter',
        cellRenderer: GammeDeliverableRendererComponent,
      },
      {
        field: 'service_provider',
        headerName: t('Service provider (optional)'),
        minWidth: 220, flex: 1,
        filter: 'agTextColumnFilter',
        editable: (p: any) => this.canEditGammeSite && !!p.data?.is_active && p.data?.gamme_is_active !== false,
        cellEditor: GammeServiceProviderEditorComponent,
      },
    ];

    this.gridOptions = {
      headerHeight: 170,
      rowHeight: 36,
      overlayLoadingTemplate: agIconLoadingTemplate,
      overlayNoRowsTemplate: `<span>${t('No regulatory obligations found')}</span>`,
      suppressMovableColumns: true,
      suppressCellFocus: true,
      getRowId: (p) => p.data.id?.toString(),
      rowClassRules: {
        'inactive-row': (p) => !p.data?.is_active,
        'gamme-unavailable-row': (p) => p.data?.gamme_is_active === false, // gamme_is_active kept to avoid conflict
      },
      sideBar: {
        toolPanels: [
          { id: 'columns', labelDefault: 'Columns', labelKey: 'columns', iconKey: 'columns', toolPanel: 'agColumnsToolPanel' },
          { id: 'filters', labelDefault: 'Filters', labelKey: 'filters', iconKey: 'filter', toolPanel: 'agFiltersToolPanel' },
        ],
      },
      defaultColDef: {
        sortable: true,
        resizable: true,
        filter: false,
        suppressHeaderMenuButton: true,
        icons: { filter: TABLE_FILTER_ICON },
      },
      context: this,
      onCellValueChanged: (event: CellValueChangedEvent) => this.onCellValueChanged(event),
      onFilterChanged: (event: FilterChangedEvent) => {
        this.filtersNb = Object.keys(event.api.getFilterModel()).length;
        this.cdRef.markForCheck();
      },
    };
  }

  public onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
    if (this.loading) event.api.showLoadingOverlay();
  }

  public clearFilters(): void {
    this.gridApi?.setFilterModel(null);
  }

  public onBuildingChange(buildingId: number | null): void {
    this.selectedBuildingId = buildingId;
    this.loadData();
  }

  private loadBuildings(): void {
    if (!this.perimeter?.sub_perimeters?.length) {
      this.loadData();
      return;
    }
    this.subscriptions.add(
      this.scopeService.perimeterTypes$.pipe(first()).subscribe(perimeterTypes => {
        this.buildings = this.perimeter.sub_perimeters.filter((p) =>
          p.is_monosite
          && !p.level_parent
          && perimeterTypes[p.perimeter_type]?.perimeter_type_class === KPI.PerimeterTypeClass.CLASS_BUILDING
        ) as unknown as KPI.Perimeter[];
        if (this.buildings.length > 0) {
          this.selectedBuildingId = this.buildings[0].id;
        }
        this.loadData();
        this.cdRef.markForCheck();
      })
    );
  }

  private loadData(): void {
    this.loading = true;
    this.gridApi?.showLoadingOverlay();
    this.cdRef.markForCheck();
    const targetId = this.selectedBuildingId || this.perimeterId;

    this.subscriptions.add(
      this.api.gamme_sites_auto_init(targetId).subscribe({
        next: (rows) => this.setRows(rows),
        error: (err) => {
          this.loading = false;
          this.gridApi?.hideOverlay();
          this.errorService.handleError(err, true);
          this.cdRef.markForCheck();
        },
      })
    );
  }

  private setRows(rows: KPI.GammeSite[]): void {
    this.rows = rows;
    this.loading = false;
    this.gridApi?.hideOverlay();
    this.cdRef.markForCheck();
  }

  private updateRow(updated: KPI.GammeSite): void {
    const idx = this.rows.findIndex((r) => r.id === updated.id);
    if (idx !== -1) {
      this.rows = [...this.rows];
      this.rows[idx] = updated;
      this.gridApi?.applyTransaction({ update: [updated] });
      this.cdRef.markForCheck();
    }
    this.saveToast.showSaved();
  }

  private onCellValueChanged(event: CellValueChangedEvent): void {
    if (!this.canEditGammeSite) return;
    const row = event.data as KPI.GammeSite;
    const field = event.colDef.field;
    if (event.newValue === event.oldValue) return;

    if (field === 'service_provider') {
      this.subscriptions.add(
        this.api.gamme_sites_patch(row.id, { service_provider: event.newValue }).subscribe({
          next: (updated) => {
            this.updateRow(updated);
            const newProvider = event.newValue as string;
            if (newProvider) {
              this.injectProviderSuggestion(newProvider);
            }
          },
          error: (err) => this.errorService.handleError(err, true),
        })
      );
    }
  }

  private injectProviderSuggestion(provider: string): void {
    const updatedRows: KPI.GammeSite[] = [];
    this.gridApi?.forEachNode((node) => {
      const row = node.data as KPI.GammeSite;
      if (!row.suggested_companies?.includes(provider)) {
        const updated = {
          ...row,
          suggested_companies: [...(row.suggested_companies ?? []), provider],
        };
        node.setData(updated);
        updatedRows.push(updated);
      }
    });
    if (updatedRows.length) {
      this.rows = this.rows.map(
        (r) => updatedRows.find((u) => u.id === r.id) ?? r
      );
    }
  }

  public toggleActive(row: KPI.GammeSite): void {
    if (!this.canEditGammeSite) return;
    this.subscriptions.add(
      this.api.gamme_sites_patch(row.id, { is_active: !row.is_active }).subscribe({
        next: (updated) => this.updateRow(updated),
        error: (err) => this.errorService.handleError(err, true),
      })
    );
  }

  public openCommentDialog(row: KPI.GammeSite): void {
    if (!this.canEditGammeSite) return;
    const dialogRef = this.dialogService.open(GammeSiteCommentDialogComponent, {
      autoFocus: false,
      closeOnBackdropClick: false,
      dialogClass: 'gamme-dialog-overlay',
      context: {
        label: row.label,
        commentLaw: row.comment_law,
        siteComment: row.comment,
      },
    });
    this.subscriptions.add(
      dialogRef.onClose.subscribe((result: { siteComment: string } | undefined) => {
        if (result !== undefined) {
          this.subscriptions.add(
            this.api.gamme_sites_patch(row.id, { comment: result.siteComment }).subscribe({
              next: (updated) => this.updateRow(updated),
              error: (err) => this.errorService.handleError(err, true),
            })
          );
        }
      })
    );
  }

}
